import { useState, useEffect } from 'react';
import screenerApi, { ScreenerStockResponse, ScreenerStockFilters } from '../services/screenerApi.ts';
import { SCREENER_PAGE_SIZE } from '../constants/screener.ts';

interface ScreenerDataResult {
    data: ScreenerStockResponse[];
    loading: boolean;
    error: string | null;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    setCurrentPage: (page: number) => void;
}

const useScreenerData = (
    screenerCode: string,
    isPremium: boolean,
    filters?: ScreenerStockFilters
): ScreenerDataResult => {
    const [data, setData] = useState<ScreenerStockResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const symbolsKey = filters?.symbols?.join(',') ?? '';
    const capturedFrom = filters?.capturedFrom;
    const capturedTo = filters?.capturedTo;
    const minLatestChangePct = filters?.minLatestChangePct;
    const sortBy = filters?.sortBy;
    const sortDir = filters?.sortDir;

    // Reset to page 1 whenever filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [symbolsKey, capturedFrom, capturedTo, minLatestChangePct, sortBy, sortDir]);

    useEffect(() => {
        if (!isPremium) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await screenerApi.getStocks(screenerCode, currentPage, SCREENER_PAGE_SIZE, {
                    symbols: symbolsKey ? symbolsKey.split(',') : undefined,
                    capturedFrom,
                    capturedTo,
                    minLatestChangePct,
                    sortBy,
                    sortDir,
                });
                setData(response.data.content);
                setTotalPages(response.data.totalPages);
                setTotalItems(response.data.totalElements);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to fetch data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [screenerCode, currentPage, isPremium, symbolsKey, capturedFrom, capturedTo, minLatestChangePct, sortBy, sortDir]);

    return { data, loading, error, currentPage, totalPages, totalItems, setCurrentPage };
};

export default useScreenerData;
